/**
 * Диалог мнения автора канала: «Добавить мнение…», «Изменить мнение…» и «Убрать мнение…»
 * (AGI Boardroom, build spec 2026-11-02 §4).
 *
 * СВОИ МАРШРУТЫ, А НЕ «ПЕРЕДЕЛАТЬ». `POST …/versions/:n/author-opinion {text}` и
 * `…/author-opinion/remove {}` — оба отвечают формой «переделка заказана», как
 * `request-changes`, но тело `request-changes` закрыто (`{prompt, targets}`), а мнение хранится
 * отдельно от переделки: одной транзакцией бэкенд заводит строку мнения и переделку с просьбой
 * `opinion:insert` / `opinion:remove`. Набрать эту просьбу руками в «Переделать…» нельзя — бэкенд
 * её отбивает.
 *
 * ПОТОЛОК — В КОДПОИНТАХ И БРЕНДА. Счётчик считает то, что считает база (`raw_text`, 0028), а
 * потолок — конфиг бренда (`brandAuthorOpinion`, не выше общего `AUTHOR_OPINION_MAX_CODEPOINTS`).
 * Годность текста проверяет схема тела из контрактов (`AuthorOpinionBodySchema`), а не копия её
 * правил: не из одних пробелов, без U+0000 и разрезанных суррогатов.
 *
 * ОТКАЗЫ 409 — ТРИ РАЗНЫХ. Бренд без сервиса генерации (`isAgentsOff`) и бренд с выключенным
 * мнением (`isAuthorOpinionOff`) — текстом сервера: черновик не менялся, и «обновите экран» не
 * помогло бы. Любой другой 409 — состояние черновика ушло из-под диалога (одобрили или отправили
 * на переделку в соседней вкладке): экран перечитывается.
 *
 * ОБЪЯСНЕНИЯ — В ⓘ. Что сервис сделает с мнением (очистка, вставка, перенос в переделки) — в
 * поповере у поля; в теле диалога только поле, счётчик и последствие нажатия.
 */

import { postLabel } from '@smm/contracts/schemas';
import { type JSX, useId, useState } from 'react';
import { ApiRequestError, post } from '../../api/client.ts';
import {
  type AuthorOpinionBody,
  AuthorOpinionBodySchema,
  type RenderModel,
  type RequestChangesResponse,
} from '../../api/types.ts';
import { isAgentsOff } from '../../brand.tsx';
import { InfoPopover } from '../../components/InfoPopover.tsx';
import { Modal } from '../../components/Modal.tsx';
import styles from './OpinionStrip.module.css';
import { isAuthorOpinionOff } from './opinionState.ts';

export type AuthorOpinionDialogMode = 'add' | 'change' | 'remove';

export interface AuthorOpinionDialogProps {
  readonly model: RenderModel;
  readonly mode: AuthorOpinionDialogMode;
  /** Чем заполнено поле при открытии: прежнее мнение, как его написали («Изменить мнение…»). */
  readonly initialText: string;
  /** Потолок мнения бренда в кодпоинтах (`brandAuthorOpinion`). */
  readonly maxCodepoints: number;
  readonly onChanged: () => void;
  readonly onClose: () => void;
}

const TITLE: Readonly<Record<AuthorOpinionDialogMode, string>> = {
  add: 'Добавить мнение',
  change: 'Изменить мнение',
  remove: 'Убрать мнение',
};

const SUBMIT: Readonly<Record<AuthorOpinionDialogMode, string>> = {
  add: 'Вставить мнение в пост',
  change: 'Заменить мнение в посте',
  remove: 'Убрать мнение из поста',
};

/** Последствие нажатия — то, что человек обязан знать до него, а не объяснение механизма. */
const CONSEQUENCE: Readonly<Record<AuthorOpinionDialogMode, string>> = {
  add:
    'Пост уйдёт на переделку: сервис генерации очистит мнение и вставит его в текст. Придёт ' +
    'новая версия — её нужно утвердить заново. Ход виден в шапке, слева от кнопки запуска.',
  change:
    'Пост уйдёт на переделку: новое мнение встанет на место прежнего. Придёт новая версия — её ' +
    'нужно утвердить заново. Ход виден в шапке, слева от кнопки запуска.',
  remove:
    'Пост уйдёт на переделку: сервис генерации уберёт мнение из текста и сгладит место, где оно ' +
    'стояло. Придёт новая версия — её нужно утвердить заново. В следующие переделки мнение больше ' +
    'не переносится.',
};

export function AuthorOpinionDialog({
  model,
  mode,
  initialText,
  maxCodepoints,
  onChanged,
  onClose,
}: AuthorOpinionDialogProps): JSX.Element {
  const fieldId = useId();
  const [text, setText] = useState(initialText);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const removing = mode === 'remove';
  const trimmed = text.trim();
  const length = [...trimmed].length;
  const over = length > maxCodepoints;
  const ready = removing || (!over && AuthorOpinionBodySchema.safeParse({ text: trimmed }).success);
  const opinion = model.author_opinion ?? null;

  const submit = (): void => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    const path = `/api/v1/drafts/${model.draft_id}/versions/${String(model.version_no)}/author-opinion`;
    const body: AuthorOpinionBody = { text: trimmed };
    (removing
      ? post<RequestChangesResponse>(`${path}/remove`, {})
      : post<RequestChangesResponse>(path, body)
    )
      .then(() => {
        onChanged();
        onClose();
      })
      .catch((e: unknown) => {
        if (
          e instanceof ApiRequestError &&
          e.status === 409 &&
          !isAgentsOff(e) &&
          !isAuthorOpinionOff(e)
        ) {
          setError('Состояние черновика изменилось — обновите экран.');
          onChanged();
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Modal
      title={TITLE[mode]}
      subtitle={`${postLabel(model.post_no)} · v${String(model.version_no)}`}
      onClose={onClose}
    >
      {removing ? (
        <div className="stack" style={{ gap: 8 }}>
          <span className="lbl" style={{ margin: 0 }}>
            Мнение в посте
          </span>
          <blockquote className={styles['quote']} data-testid="opinion-remove-text">
            {opinion?.cleaned ?? opinion?.raw ?? ''}
          </blockquote>
        </div>
      ) : (
        <div className="stack" style={{ gap: 8 }}>
          <div className={styles['fieldHead']}>
            <label className="lbl" htmlFor={fieldId} style={{ margin: 0 }}>
              Мнение — простым текстом, как вы его скажете
            </label>
            <InfoPopover label="Как сервис обработает мнение">
              <b>Слова остаются вашими</b>
              <span>
                Сервис генерации исправит грамматику и пунктуацию, уберёт слова-паразиты и приведёт
                знаки к нормам канала; адреса сайтов запишет словами, без ссылок. Слова, «я», факты
                и обороты — ваши.
              </span>
              <span>
                Мнение встанет в пост без пометки — там, где оно к месту. Ваша позиция главнее
                рассуждения поста: совпадающее сольётся с вашим текстом, противоречащее уйдёт.
              </span>
              <span>
                Мнение хранится отдельно и переносится в каждую следующую переделку дословно. У
                поста одно мнение: новое заменяет прежнее.
              </span>
            </InfoPopover>
          </div>
          <textarea
            id={fieldId}
            className="inp"
            rows={7}
            value={text}
            placeholder="Что вы об этом думаете — своими словами."
            onChange={(e) => {
              setText(e.target.value);
            }}
          />
          <div className="lmeter">
            <span
              className={over ? `cnt num ${styles['over']}` : 'cnt num'}
              data-testid="opinion-counter"
            >
              {length} / {maxCodepoints}
            </span>
            <span className="unit">символов</span>
          </div>
          {over ? (
            <div className="val val-err" role="alert">
              <span>{`Мнение длиннее ${String(maxCodepoints)} символов — сократите его: длиннее бренд не принимает.`}</span>
            </div>
          ) : null}
        </div>
      )}

      <div className="notice">{CONSEQUENCE[mode]}</div>

      {error === null ? null : (
        <div className="val val-err">
          <span>{error}</span>
        </div>
      )}

      <div className="stack" style={{ gap: 10 }}>
        <button
          type="button"
          className={ready && !busy ? 'btn btn-pri btn-w' : 'btn btn-w btn-dis'}
          disabled={!ready || busy}
          onClick={submit}
        >
          {SUBMIT[mode]}
        </button>
        <button type="button" className="btn btn-ghost btn-w" onClick={onClose}>
          Отмена
        </button>
      </div>
    </Modal>
  );
}
